/**
 * Run history page — Phase 1.8 Pass 5.
 *
 * Lists `keepa_search_runs` rows for the current tenant. Per-row:
 *   - status badge (queued / running / succeeded / failed / cancelled)
 *   - trigger chip color-coded by source (schedule / template / manual)
 *   - quick stats (results, tokens, estimated tokens)
 *   - "View products" link to /products?searchRunId=<id>
 *   - Cancel (queued) or Retry (failed) actions
 *
 * Filter pills mirror the audit-log pill pattern (multi-select via comma).
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, RefreshCw, X } from 'lucide-react';
import type { KeepaSearchRun } from '@sourcito/schemas';
import { Badge, Button, Card } from '../design';
import {
  useCancelKeepaSearchRun,
  useKeepaSearchRuns,
  useRetryKeepaSearchRun,
} from '../lib/keepaSearchRuns';

type StatusFilter = 'all' | 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function RunsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const { data, isLoading, isError, error, refetch, isFetching } = useKeepaSearchRuns({
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: 100,
  });

  const rows = data ?? [];

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/search"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Run history</h1>
              <p className="text-xs text-fg-tertiary">
                Every queued + executed Keepa search, with telemetry + retry.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-md bg-surface-elev border border-border-subtle p-0.5">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-xs transition-colors duration-fast ${
                    statusFilter === opt.value
                      ? 'bg-accent/[0.18] text-accent-fg'
                      : 'text-fg-secondary hover:text-fg-primary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Refresh"
              onClick={() => refetch()}
              loading={isFetching}
            >
              <RefreshCw className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-10 space-y-4">
        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">Failed to load runs: {(error as Error).message}</p>
          </Card>
        )}

        {data && rows.length === 0 && (
          <Card className="text-center py-10">
            <p className="text-sm text-fg-secondary">
              No runs match this filter.{' '}
              {statusFilter === 'all' ? (
                <>
                  <Link to="/search" className="text-accent-fg hover:underline">
                    Run a search
                  </Link>{' '}
                  to start populating run history.
                </>
              ) : (
                'Try clearing the filter.'
              )}
            </p>
          </Card>
        )}

        {rows.length > 0 && (
          <div className="border border-border-subtle rounded-md bg-surface overflow-hidden">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b border-border-subtle bg-surface-elev">
                <tr className="text-xs uppercase tracking-wider text-fg-tertiary">
                  <th className="text-left px-4 py-2.5 font-medium">Queued</th>
                  <th className="text-left px-4 py-2.5 font-medium">Trigger</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Results</th>
                  <th className="text-right px-4 py-2.5 font-medium">New</th>
                  <th className="text-right px-4 py-2.5 font-medium">Keepa tokens</th>
                  <th className="text-left px-4 py-2.5 font-medium">Run</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <RunRow key={r.id} run={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function RunRow({ run }: { run: KeepaSearchRun }) {
  const cancel = useCancelKeepaSearchRun();
  const retry = useRetryKeepaSearchRun();
  const queuedAt = run.queuedAt ? new Date(run.queuedAt) : null;

  return (
    <tr className="border-t border-border-subtle hover:bg-surface-elev align-top">
      <td className="px-4 py-2.5 text-xs text-fg-secondary">
        {queuedAt ? (
          <time dateTime={run.queuedAt!} title={queuedAt.toLocaleString()}>
            {relativeTime(queuedAt)}
          </time>
        ) : (
          '—'
        )}
      </td>
      <td className="px-4 py-2.5">
        <TriggerBadge trigger={run.trigger} />
      </td>
      <td className="px-4 py-2.5">
        <StatusBadge status={run.status} errorMessage={run.errorMessage} />
      </td>
      <td className="px-4 py-2.5 text-right">{run.resultCount.toLocaleString()}</td>
      <td className="px-4 py-2.5 text-right">{run.newProductCount.toLocaleString()}</td>
      <td className="px-4 py-2.5 text-right text-xs">
        {run.keepaTokensConsumed !== null ? (
          <span className="text-fg-primary">
            {run.keepaTokensConsumed.toLocaleString()}
            <span className="text-fg-tertiary"> / ~{run.estimatedKeepaTokens}</span>
          </span>
        ) : (
          <span className="text-fg-tertiary">~{run.estimatedKeepaTokens} est.</span>
        )}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          {run.status === 'succeeded' && run.resultCount > 0 && (
            // TanStack's `<Link to="/products" search={...}>` would require us
            // to declare validateSearch on the products route just for this
            // one optional filter. Plain anchor is simpler and the SPA reads
            // the param via the same URLSearchParams the products hook uses.
            <a
              href={`/products?searchRunId=${run.id}`}
              className="text-xs font-medium text-accent-fg hover:underline"
            >
              View products →
            </a>
          )}
          {run.status === 'queued' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => cancel.mutate(run.id)}
              loading={cancel.isPending}
              leadingIcon={<X className="size-3.5" />}
            >
              Cancel
            </Button>
          )}
          {run.status === 'failed' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => retry.mutate(run.id)}
              loading={retry.isPending}
            >
              Retry
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function TriggerBadge({ trigger }: { trigger: KeepaSearchRun['trigger'] }) {
  const config =
    trigger === 'schedule'
      ? { variant: 'accent' as const, label: 'schedule' }
      : trigger === 'template' || trigger === 'template_queued'
        ? { variant: 'positive' as const, label: 'template' }
        : { variant: 'neutral' as const, label: 'manual' };
  return (
    <Badge variant={config.variant} className="text-[10px] uppercase tracking-wider">
      {config.label}
    </Badge>
  );
}

function StatusBadge({
  status,
  errorMessage,
}: {
  status: KeepaSearchRun['status'];
  errorMessage: string | null;
}) {
  const variant =
    status === 'succeeded'
      ? 'positive'
      : status === 'failed'
        ? 'negative'
        : status === 'running'
          ? 'accent'
          : status === 'cancelled'
            ? 'neutral'
            : 'warning';
  return (
    <Badge
      variant={variant}
      className="text-[10px] uppercase tracking-wider"
      title={status === 'failed' && errorMessage ? errorMessage : status}
    >
      {status}
    </Badge>
  );
}

function relativeTime(date: Date): string {
  const diff = Date.now() - date.getTime();
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (diff < min) return 'just now';
  if (diff < hour) return `${Math.round(diff / min)}m ago`;
  if (diff < day) return `${Math.round(diff / hour)}h ago`;
  return `${Math.round(diff / day)}d ago`;
}
